import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';

import { ContractState } from '../../../../types/ContractMessage';
import { CustomerCompanyMessage } from '../../../../types/CustomerCompanyMessage';
import { CustomerGroupMessage } from '../../../../types/CustomerGroupMessage';
import { BasicFilterContainer } from '../../../cdl/Filter/BasicFilterContainer/BasicFilterContainer';
import { FilterMultiSelectFull } from '../../../cdl/Filter/FilterMultiSelectFull/FilterMultiSelectFull';
import { FilterMultiSelectShort } from '../../../cdl/Filter/FilterMultiSelectShort/FilterMultiSelectShort';
import { FilterNumeric } from '../../../cdl/Filter/FilterNumeric/FilterNumeric';
import { FilterRadioGroup } from '../../../cdl/Filter/FilterRadioGroup/FilterRadioGroup';
import { FilterSelectFull } from '../../../cdl/Filter/FilterSelectFull/FilterSelectFull';
import { FilterText } from '../../../cdl/Filter/FilterText/FilterText';
import { isEmptyFilterValue } from '../../../cdl/Filter/helper/isEmptyFilterValue';
import { normalizeFilterValue } from '../../../cdl/Filter/helper/normalizeFilterValue';
import { queries } from '../../../common/api/queryKeys/queries';
import { translate } from '../../../common/helpers/translate.helper';
import { useRole } from '../../../common/hooks/useRole';
import { ContractFilterKeys, LUBES_CONTRACT_FILTER_KEYS } from '../../../contracts/overview/contractsFilterKeys';
import { useAssignedSuppliersOptions } from '../../../contracts/overview/hooks/useAssignedSuppliersOptions';
import { fromApiParams, toApiParams } from '../../../contracts/overview/utils/expirationFilterMapper';

export const ContractsFilterPills = () => {
    const search = useSearch({ from: '/_app/contracts/' });
    const navigate = useNavigate({ from: '/contracts' });
    const role = useRole();
    const isSuperAdmin = role.isSuperAdmin();
    const suppliersQuery = useAssignedSuppliersOptions();
    const customerGroupsQuery = useQuery({
        ...queries.customerGroups.list(),
        enabled: isSuperAdmin,
        select: (groups: CustomerGroupMessage[]) => groups.map((group) => ({ label: group.name, value: group.id })),
    });
    const showCustomerFilter = isSuperAdmin || !role.isOneCompanyUser();
    const customersQuery = useQuery({
        ...queries.customers.list(),
        enabled: isSuperAdmin,
        select: (customers: CustomerCompanyMessage[]) =>
            customers.map((customer) => ({ label: customer.internalName || customer.name, value: customer.id })),
    });
    const customerOptions = isSuperAdmin
        ? customersQuery.data ?? []
        : role.getCompanies().map((company) => ({ label: company.internalName || company.name, value: company.id }));

    const expirationFilterValue = fromApiParams(search.validUntilFrom, search.validUntilTo);
    const hasActiveFilters = LUBES_CONTRACT_FILTER_KEYS.some((key) => !isEmptyFilterValue(search[key]));

    const setFilter = (key: ContractFilterKeys, value: unknown) => {
        navigate({
            search: (previousSearch) => ({
                ...previousSearch,
                [key]: normalizeFilterValue(value),
                page: 0,
            }),
        });
    };

    const clearFilters = () => {
        navigate({
            search: (previousSearch) => ({
                ...previousSearch,
                ...Object.fromEntries(LUBES_CONTRACT_FILTER_KEYS.map((key) => [key, undefined])),
                page: 0,
            }),
        });
    };

    return (
        <BasicFilterContainer hasActiveFilters={hasActiveFilters} onClear={clearFilters}>
            {isSuperAdmin ? (
                <FilterSelectFull
                    label={translate('contracts.filters.customerGroup')}
                    filterByLabel={translate('contracts.filters.filterByCustomerGroup')}
                    filterPlaceholder={translate('contracts.filters.searchForCustomerGroup')}
                    options={customerGroupsQuery.data ?? []}
                    value={search.customerGroupId}
                    onChange={(value) => setFilter(ContractFilterKeys.CUSTOMER_GROUP_ID, value)}
                    isLoading={customerGroupsQuery.isPending}
                    isError={customerGroupsQuery.isError}
                />
            ) : null}
            <FilterText
                label={translate('contracts.filters.nameReference')}
                filterByLabel={translate('contracts.filters.filterByNameReference')}
                value={search.searchQuery}
                onChange={(value) => setFilter(ContractFilterKeys.SEARCH_QUERY, value)}
            />
            <FilterMultiSelectFull
                label={translate('contracts.filters.supplier')}
                filterByLabel={translate('contracts.filters.filterBySupplier')}
                filterPlaceholder={translate('contracts.filters.searchForSupplier')}
                options={suppliersQuery.data ?? []}
                values={search.supplierIds}
                onChange={(values) => setFilter(ContractFilterKeys.SUPPLIER_IDS, values)}
                isLoading={suppliersQuery.isPending}
                isError={suppliersQuery.isError}
            />
            {showCustomerFilter ? (
                <FilterMultiSelectFull
                    label={translate(isSuperAdmin ? 'contracts.filters.customer' : 'contracts.filters.company')}
                    filterByLabel={translate(
                        isSuperAdmin ? 'contracts.filters.filterByCustomer' : 'contracts.filters.filterByCompany'
                    )}
                    filterPlaceholder={translate(
                        isSuperAdmin ? 'contracts.filters.searchForCustomer' : 'contracts.filters.searchForCompany'
                    )}
                    options={customerOptions}
                    values={search.customerIds}
                    onChange={(values) => setFilter(ContractFilterKeys.CUSTOMER_IDS, values)}
                    isLoading={isSuperAdmin && customersQuery.isPending}
                    isError={isSuperAdmin && customersQuery.isError}
                />
            ) : null}
            <FilterNumeric
                label={translate('contracts.filters.expiration')}
                filterByLabel={translate('contracts.filters.filterByExpiration')}
                value={expirationFilterValue}
                onChange={(value) => {
                    const expirationParams = toApiParams(value);
                    navigate({
                        search: (previousSearch) => ({
                            ...previousSearch,
                            [ContractFilterKeys.VALID_UNTIL_FROM]: normalizeFilterValue(
                                expirationParams.validUntilFrom
                            ),
                            [ContractFilterKeys.VALID_UNTIL_TO]: normalizeFilterValue(expirationParams.validUntilTo),
                            page: 0,
                        }),
                    });
                }}
                inputTrailingVisual={translate('contracts.filters.days')}
            />
            <FilterRadioGroup
                label={translate('contracts.filters.evergreen')}
                filterByLabel={translate('contracts.filters.filterByEvergreen')}
                options={[
                    { label: translate('global.yes'), value: true },
                    { label: translate('global.no'), value: false },
                ]}
                value={search.evergreen}
                onChange={(value) => setFilter(ContractFilterKeys.EVERGREEN, value)}
            />
            <FilterMultiSelectShort<ContractState>
                label={translate('contracts.filters.status')}
                filterByLabel={translate('contracts.filters.filterByStatus')}
                options={Object.values(ContractState).map((state) => ({
                    label: translate(`contracts.status.${state}`),
                    value: state,
                }))}
                values={search.states}
                onChange={(values) => setFilter(ContractFilterKeys.STATES, values)}
            />
        </BasicFilterContainer>
    );
};
